import {
  AdjusterDetail,
  BenefitDetail,
  CarrierDetail,
  ClaimDetail,
  type ContactHandleSchema,
  CoverageCategory,
  CoverageLiabilityStatus,
  CoverageSchema,
  PolicyDetail,
  PolicyIdentifier,
  PolicyType,
  SubrogatorDetail,
  VehicleDetail,
  type WebSource,
} from '@/services/types/coverage-types';

export interface CoverageFormData {
  category: CoverageCategory | null;
  policy_type: PolicyType | null;
  liability_status: CoverageLiabilityStatus | null;
  denial_reason: string | null;
  carrier_v2_id: string | null;
  carrier_division_id: string | null;
  carrier: CarrierDetail;
  policy_identifiers: PolicyIdentifier[];
  named_insured: { name: string }[];
  benefits: BenefitDetail[];
  adjusters: AdjusterDetail[];
  claims: ClaimDetail[];
  vehicles: VehicleDetail[];
  policy_details: PolicyDetail[];
  subrogator: SubrogatorDetail;
}

const cloneOptionalSources = (sources?: WebSource[]): WebSource[] | undefined =>
  sources?.map((source) => ({ ...source }));

const cloneNullableSources = (
  sources: WebSource[] | null | undefined,
): WebSource[] | null => {
  if (sources === undefined) {
    return null;
  }
  if (sources === null) {
    return null;
  }
  return sources.map((source) => ({ ...source }));
};

const cloneHandles = (
  handles: ContactHandleSchema[] | null | undefined,
): ContactHandleSchema[] | null => {
  if (!handles) {
    return handles ?? null;
  }
  return handles.map((handle) => ({
    ...handle,
    sources: cloneOptionalSources(handle.sources),
  }));
};

const cloneCarrierDetail = (
  carrier: CarrierDetail | null | undefined,
): CarrierDetail => ({
  name: carrier?.name ?? null,
  handles: cloneHandles(carrier?.handles),
  address: carrier?.address ?? null,
});

const cloneSubrogator = (
  subrogator: SubrogatorDetail | null | undefined,
): SubrogatorDetail => ({
  name: subrogator?.name ?? null,
  handles: cloneHandles(subrogator?.handles),
  sources: cloneNullableSources(subrogator?.sources),
});

const clonePolicyIdentifiers = (
  identifiers: PolicyIdentifier[] | null | undefined,
): PolicyIdentifier[] =>
  identifiers?.map((identifier) => ({ ...identifier })) ?? [];

const cloneBenefits = (
  benefits: BenefitDetail[] | null | undefined,
): BenefitDetail[] =>
  benefits?.map((benefit) => ({
    ...benefit,
    limitations: benefit.limitations ? { ...benefit.limitations } : null,
    is_stacked: benefit.is_stacked ?? null,
  })) ?? [];

const cloneAdjusters = (
  adjusters: AdjusterDetail[] | null | undefined,
): AdjusterDetail[] =>
  adjusters?.map((adjuster) => ({
    ...adjuster,
    handles: cloneHandles(adjuster.handles),
  })) ?? [];

const cloneClaims = (claims: ClaimDetail[] | null | undefined): ClaimDetail[] =>
  claims?.map((claim) => ({ ...claim })) ?? [];

const cloneVehicles = (
  vehicles: VehicleDetail[] | null | undefined,
): VehicleDetail[] => vehicles?.map((vehicle) => ({ ...vehicle })) ?? [];

const clonePolicyDetails = (
  details: PolicyDetail[] | null | undefined,
): PolicyDetail[] => details?.map((detail) => ({ ...detail })) ?? [];

export const createCoverageFormDefaults = (): CoverageFormData => ({
  category: null,
  policy_type: null,
  liability_status: null,
  denial_reason: null,
  carrier_v2_id: null,
  carrier_division_id: null,
  carrier: {
    name: null,
    handles: null,
    address: null,
  },
  policy_identifiers: [],
  named_insured: [{ name: '' }],
  benefits: [],
  adjusters: [],
  claims: [],
  vehicles: [],
  policy_details: [],
  subrogator: {
    name: null,
    handles: null,
    sources: null,
  },
});

export const mapCoverageToFormValues = (
  coverage?: CoverageSchema | null,
): CoverageFormData => {
  const defaults = createCoverageFormDefaults();

  if (!coverage) {
    return defaults;
  }

  return {
    ...defaults,
    category: coverage.category,
    policy_type: coverage.policy_type,
    liability_status: coverage.liability_status,
    denial_reason: coverage.denial_reason ?? null,
    carrier_v2_id: coverage.carrier_v2_id ?? null,
    carrier_division_id: coverage.carrier_division_id ?? null,
    carrier: cloneCarrierDetail(coverage.carrier),
    policy_identifiers: clonePolicyIdentifiers(coverage.policy_identifiers),
    named_insured:
      coverage.named_insured && coverage.named_insured.length > 0
        ? coverage.named_insured.map((name) => ({ name }))
        : defaults.named_insured,
    benefits: cloneBenefits(coverage.benefits),
    adjusters: cloneAdjusters(coverage.adjusters),
    claims: cloneClaims(coverage.claims),
    vehicles: cloneVehicles(coverage.vehicles),
    policy_details: clonePolicyDetails(coverage.policy_details),
    subrogator: cloneSubrogator(coverage.subrogator),
  };
};
